import { useEffect, useState } from "react";
import { cn } from "./ui";
import { money, type BooksDoc } from "./deals-api";
import { DOC_STATUS, docTitle, financeApi, type CustomerFinance, type FinanceParent } from "./finance-api";
import { locale, t, tn } from "@/lib/i18n";

/* Этап 6: блок «Финансы Books» в карточке компании и контакта и строки документов Books (общие с карточкой сделки).
   Всё только чтение: заказы, счета, оплаты и кредит-ноты зеркалятся из Zoho Books каждые 15 минут. */

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(locale) : "—");
const days = (n: number) => tn(n, "день", "дня", "дней");

const ICON: Record<BooksDoc["kind"], string> = {
  invoice: "ri-bill-line",
  salesorder: "ri-shopping-cart-2-line",
  payment: "ri-money-dollar-circle-line",
  creditnote: "ri-refund-2-line",
};

/** строка документа Books: вид, номер со ссылкой в Books, дата, статус (у оплаты — способ и счета), сумма, долг */
export function DocRow({ o }: { o: BooksDoc }) {
  const isPay = o.kind === "payment";
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 text-[12px]">
      <i className={cn(ICON[o.kind], isPay ? "text-emerald-600" : "text-muted-foreground")} aria-hidden />
      <a href={o.booksUrl} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">
        {docTitle(o)}
      </a>
      <span className="tabular-nums text-muted-foreground">{day(o.date)}</span>
      <span className="text-muted-foreground">
        {isPay ? o.mode || DOC_STATUS[o.status] || o.status : DOC_STATUS[o.status] ?? o.status}
        {o.invoiceNumbers.length ? ` → ${o.invoiceNumbers.join(", ")}` : ""}
      </span>
      <span className={cn("ms-auto tabular-nums", isPay && "text-emerald-600")}>
        {isPay ? "+" : o.kind === "creditnote" ? "−" : ""}
        {money(o.total, o.currency)}
      </span>
      {o.kind === "invoice" && o.balance > 0.5 ? (
        <span className={cn("tabular-nums", o.status === "overdue" ? "text-red-600" : "text-amber-600")}>{t("долг {sum}", { sum: money(o.balance, o.currency) })}</span>
      ) : null}
    </li>
  );
}

const KINDS: { id: "all" | BooksDoc["kind"]; label: string }[] = [
  { id: "all", label: t("Все") },
  { id: "invoice", label: t("Счета") },
  { id: "payment", label: t("Оплаты") },
  { id: "salesorder", label: t("Заказы") },
  { id: "creditnote", label: t("Кредит-ноты") },
];

export function FinanceBlock({ parent, title }: { parent: FinanceParent; title?: string }) {
  const [data, setData] = useState<CustomerFinance | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "scope" | "error">("loading");
  const [kind, setKind] = useState<(typeof KINDS)[number]["id"]>("all");
  const [all, setAll] = useState(false);
  const key = "companyId" in parent ? `c:${parent.companyId}` : "contactId" in parent ? `p:${parent.contactId}` : `b:${parent.booksId}`;

  useEffect(() => {
    let live = true;
    setState("loading");
    setAll(false);
    setKind("all");
    financeApi.customer(parent).then(
      (r) => {
        if (!live) return;
        setData(r);
        setState("ok");
      },
      (e: { status?: number }) => {
        if (!live) return;
        setState(e?.status === 403 ? "scope" : "error");
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (state === "scope") {
    return (
      <section className="mt-5" aria-label={title ?? t("Финансы Books")}>
        <h4 className="text-[13px] font-semibold">{title ?? t("Финансы Books")}</h4>
        <p className="mt-1 text-[12px] text-muted-foreground">{t("Финансы этого клиента видят его продавец и сотрудники с правом «Финансы всех клиентов».")}</p>
      </section>
    );
  }
  if (state !== "ok" || !data) {
    return (
      <section className="mt-5" aria-label={title ?? t("Финансы Books")}>
        <h4 className="text-[13px] font-semibold">{title ?? t("Финансы Books")}</h4>
        <p className="mt-1 text-[12px] text-muted-foreground">{state === "error" ? t("Не удалось загрузить финансы.") : t("Загрузка…")}</p>
      </section>
    );
  }
  const s = data.summary;
  const cur = s.currency;
  const docs = kind === "all" ? data.docs : data.docs.filter((o) => o.kind === kind);
  const shown = all ? docs : docs.slice(0, 8);
  const tiles = [
    { label: t("Выручка (счета)"), value: s.revenue ? money(s.revenue, cur) : "—", sub: s.revenue12m ? t("за 12 мес {sum}", { sum: money(s.revenue12m, cur) }) : "", tone: "" },
    {
      label: t("Долг сейчас"),
      value: s.receivable > 0.5 ? money(s.receivable, cur) : t("нет"),
      sub: s.overdue > 0.5 ? t("просрочено {sum} · {d}", { sum: money(s.overdue, cur), d: days(s.overdueDays) }) : s.openInvoices ? t("{n} счетов к оплате", { n: s.openInvoices }) : "",
      tone: s.overdue > 0.5 ? "text-red-600" : s.receivable > 0.5 ? "text-amber-600" : "",
    },
    { label: t("Средний чек"), value: s.avgCheck ? money(s.avgCheck, cur) : "—", sub: s.invoices ? t("{n} счетов", { n: s.invoices }) : "", tone: "" },
    {
      label: t("Последняя оплата"),
      value: day(s.lastPaymentAt),
      sub: s.avgPayDays !== null ? t("платят в среднем за {d}", { d: days(s.avgPayDays) }) : s.lastOrderAt ? t("последний заказ {date}", { date: day(s.lastOrderAt) }) : "",
      tone: "",
    },
  ];
  return (
    <section className="mt-5" aria-label={title ?? t("Финансы Books")}>
      <h4 className="text-[13px] font-semibold">
        {title ?? t("Финансы Books")}
        {data.total ? ` · ${data.total}` : ""}
      </h4>
      {data.customers.length === 0 ? (
        <p className="mt-1 text-[12px] text-muted-foreground">{t("Не связан с клиентом Zoho Books — связать можно в разделе «Zoho».")}</p>
      ) : (
        <>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            {t("Клиент Books:")}{" "}
            {data.customers.map((c, i) => (
              <span key={c.booksId}>
                {i ? ", " : ""}
                <a href={c.booksUrl} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary hover:underline">
                  {c.name}
                </a>
              </span>
            ))}
            {" · "}
            {t("обновляется каждые 15 минут")}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {tiles.map((k) => (
              <div key={k.label} className="rounded-btn border border-border bg-background/60 px-3 py-2">
                <p className="text-[11px] font-semibold text-muted-foreground">{k.label}</p>
                <p className={cn("mt-0.5 text-[15px] font-semibold tabular-nums", k.tone)}>{k.value}</p>
                {k.sub ? <p className="text-[11px] text-muted-foreground">{k.sub}</p> : null}
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {KINDS.map((k) => {
              const n = k.id === "all" ? data.total : s.counts[k.id];
              if (k.id !== "all" && !n) return null;
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => setKind(k.id)}
                  className={cn(
                    "h-7 cursor-pointer rounded-full border px-2.5 text-[11.5px] font-semibold transition",
                    kind === k.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {k.label} · {n}
                </button>
              );
            })}
          </div>
          <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
            {shown.map((o) => (
              <DocRow key={o.id} o={o} />
            ))}
            {docs.length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Документов нет.")}</li> : null}
          </ul>
          {docs.length > shown.length ? (
            <button type="button" onClick={() => setAll(true)} className="mt-1.5 cursor-pointer text-[12px] font-semibold text-primary hover:underline">
              {t("Показать все ({n})", { n: docs.length })}
            </button>
          ) : null}
          {data.total > data.docs.length ? <p className="mt-1 text-[11px] text-muted-foreground">{t("Показаны последние {n} документов; остальные — в Zoho Books.", { n: data.docs.length })}</p> : null}
        </>
      )}
    </section>
  );
}
